import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {
  GalleryContainer,
  MainImageContainer,
  MainImage,
  PlaceholderIcon,
  ThumbnailGrid,
  Thumbnail,
  ThumbnailLabel,
  Lightbox,
  LightboxImage,
  CloseButton,
  NavButton,
} from './TyreImageGallery.style';

class TyreImageGallery extends Component {
  static propTypes = {
    mainImage: PropTypes.string,
    media: PropTypes.object,
  };

  state = {
    selectedImage: null,
    lightboxOpen: false,
    images: [],
    failedImages: new Set(),
    currentImageIndex: 0,
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.mainImage !== this.props.mainImage ||
      prevProps.media !== this.props.media
    ) {
      this.prepareImages();
    }
  }

  prepareImages = () => {
    const {mainImage, media} = this.props;
    const images = [];

    // Add main image
    if (mainImage) {
      images.push({url: mainImage, label: 'Main View', type: 'main'});
    }

    // Add media images
    if (media) {
      if (media.frontView) {
        images.push({url: media.frontView, label: 'Front View', type: 'front'});
      }
      if (media.cutNoRim) {
        images.push({url: media.cutNoRim, label: 'Cut (No Rim)', type: 'cut'});
      }
      if (media.noRim) {
        images.push({url: media.noRim, label: 'No Rim', type: 'norim'});
      }

      // Add additional visuals
      if (media.additionalVisuals && media.additionalVisuals.length > 0) {
        media.additionalVisuals.forEach((visual, index) => {
          images.push({
            url: visual,
            label: `Visual ${index + 1}`,
            type: 'additional',
          });
        });
      }

      // Add key visuals
      if (media.keyVisuals && media.keyVisuals.length > 0) {
        media.keyVisuals.forEach((visual, index) => {
          images.push({url: visual, label: `Key ${index + 1}`, type: 'key'});
        });
      }

      // Add product videos
      if (media.productVideo && media.productVideo.length > 0) {
        media.productVideo.forEach((video, index) => {
          images.push({
            url: video,
            label: `Video ${index + 1}`,
            type: 'video',
            isVideo: true,
          });
        });
      }
    }

    // Remove duplicates
    const uniqueImages = images.filter(
      (img, index, self) => index === self.findIndex((t) => t.url === img.url)
    );

    this.setState({
      images: uniqueImages,
      selectedImage: uniqueImages.length > 0 ? uniqueImages[0].url : null,
      currentImageIndex: 0,
      failedImages: new Set(),
    });
  };

  handleThumbnailClick = (imageUrl, index) => {
    this.setState({
      selectedImage: imageUrl,
      currentImageIndex: index,
    });
  };

  handleImageClick = () => {
    if (this.state.selectedImage) {
      this.setState({lightboxOpen: true});
    }
  };

  handleCloseLightbox = () => {
    this.setState({lightboxOpen: false});
  };

  handleImageError = (imageUrl) => {
    this.setState((prevState) => {
      const failedImages = new Set(prevState.failedImages);
      failedImages.add(imageUrl);
      
      // Filter out failed images
      const validImages = prevState.images.filter(img => !failedImages.has(img.url));
      
      // Update selected image if current one failed
      const newSelectedImage = failedImages.has(prevState.selectedImage) && validImages.length > 0
        ? validImages[0].url
        : prevState.selectedImage;
      
      return {
        failedImages,
        images: validImages,
        selectedImage: newSelectedImage,
      };
    });
  };

  handleNextImage = (e) => {
    e.stopPropagation();
    const {images, currentImageIndex} = this.state;
    const nextIndex = (currentImageIndex + 1) % images.length;
    this.setState({
      currentImageIndex: nextIndex,
      selectedImage: images[nextIndex].url,
    });
  };

  handlePrevImage = (e) => {
    e.stopPropagation();
    const {images, currentImageIndex} = this.state;
    const prevIndex = (currentImageIndex - 1 + images.length) % images.length;
    this.setState({
      currentImageIndex: prevIndex,
      selectedImage: images[prevIndex].url,
    });
  };

  handleKeyDown = (e) => {
    if (!this.state.lightboxOpen) return;
    
    if (e.key === 'ArrowRight') {
      this.handleNextImage(e);
    } else if (e.key === 'ArrowLeft') {
      this.handlePrevImage(e);
    } else if (e.key === 'Escape') {
      this.handleCloseLightbox();
    }
  };

  componentDidMount() {
    this.prepareImages();
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  render() {
    const {selectedImage, lightboxOpen, images} = this.state;

    return (
      <>
        <GalleryContainer>
          <MainImageContainer
            hasImage={!!selectedImage}
            onClick={this.handleImageClick}
          >
            {selectedImage ? (
              images[this.state.currentImageIndex]?.isVideo ? (
                <video
                  src={selectedImage}
                  controls
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <MainImage src={selectedImage} alt="Tyre" />
              )
            ) : (
              <PlaceholderIcon>
                <i className="mdi mdi-image-outline" />
              </PlaceholderIcon>
            )}
          </MainImageContainer>

          {images.length > 1 && (
            <ThumbnailGrid>
              {images.map((image, index) => (
                <div key={index}>
                  <Thumbnail
                    selected={selectedImage === image.url}
                    onClick={() => this.handleThumbnailClick(image.url, index)}
                  >
                    {image.isVideo ? (
                      <video
                        src={image.url}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={() => this.handleImageError(image.url)}
                      />
                    ) : (
                      <img 
                        src={image.url} 
                        alt={image.label}
                        onError={() => this.handleImageError(image.url)}
                      />
                    )}
                    {image.isVideo && (
                      <i
                        className="mdi mdi-play-circle"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '24px',
                          color: 'white',
                          textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </Thumbnail>
                  <ThumbnailLabel selected={selectedImage === image.url}>
                    {image.label}
                  </ThumbnailLabel>
                </div>
              ))}
            </ThumbnailGrid>
          )}
        </GalleryContainer>

        {lightboxOpen && (
          <Lightbox onClick={this.handleCloseLightbox}>
            <CloseButton onClick={this.handleCloseLightbox}>
              <i className="mdi mdi-close" />
            </CloseButton>
            {images.length > 1 && (
              <>
                <NavButton 
                  direction="left" 
                  onClick={this.handlePrevImage}
                  title="Previous image (Left arrow)"
                >
                  <i className="mdi mdi-chevron-left" />
                </NavButton>
                <NavButton 
                  direction="right" 
                  onClick={this.handleNextImage}
                  title="Next image (Right arrow)"
                >
                  <i className="mdi mdi-chevron-right" />
                </NavButton>
              </>
            )}
            {images[this.state.currentImageIndex]?.isVideo ? (
              <video
                src={selectedImage}
                controls
                autoPlay
                style={{
                  maxWidth: '90%',
                  maxHeight: '90%',
                  objectFit: 'contain',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                  borderRadius: '8px',
                }}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <LightboxImage src={selectedImage} alt="Tyre" />
            )}
          </Lightbox>
        )}
      </>
    );
  }
}

export default TyreImageGallery;
